/**
 * カレンダー操作クラス
 */
import * as time from '@/libs/time'
import * as C from '@/consts'

export default class Calendar {
  /**
   * コンストラクタ
   *
   * @param {Object} settings 設定情報
   */
  constructor (settings) {
    // カテゴリ情報の設定する
    this.daybreak = settings.daybreak
    this.span = { ...settings.span }

    // 日付情報を設定する
    this.dates = settings.dates
    if (settings.anytime) {
      // 24H 用に dates を調整する
      this.adjustAnytime()
    }

    // 予約可能期間を日付情報を元に正規化する
    // cat.allow が営業時間外を指さないことを保証する
    this.allow = {
      begin: this.nextOpenTime(settings.allow.begin),
      end: this.previousOpenTime(settings.allow.end)
    }
  }

  /**
   * 出発日時の入力可能範囲を返す
   *
   * @returns {Object} 出発日時の入力可能範囲を返す
   */
  getAllowBegin () {
    // span に基づいて期間を計算する
    const cend = this.sub(this.allow.end, this.span.min)

    // 営業状態で補正する
    return {
      begin: this.allow.begin,
      end: this.previousOpenTime(cend)
    }
  }

  /**
   * 帰着日時の入力可能範囲を返す
   *
   * @param {String} begin 出発日時
   * @returns {Object} 帰着日時の入力可能範囲を返す
   */
  getAllowEnd (begin) {
    // span に基づいて期間を計算する
    const cbegin = this.add(begin, this.span.min)
    let cend = this.add(begin, this.span.max)

    // allow の範囲に入るように調整する
    if (this.allow.end < cend) {
      cend = this.allow.end
    }

    return {
      begin: this.nextOpenTime(cbegin),
      end: this.previousOpenTime(cend)
    }
  }

  /**
   * 出発, 帰着日時のデフォルト値を返す
   *
   * @param {String} base 基準日
   */
  getDefaultSchedule (base = '') {
    // allow の期間が 24H 未満なら allow を返す
    if ((this.allow.end - this.allow.begin) < time.DAY) {
      return { ...this.allow }
    }

    // base が定義されていなければ、allow.begin を設定する
    if (!base) {
      base = time.format('Y-m-d', this.allow.begin)
    }

    const begin = this.getDefaultBegin(base)
    const end = this.getDefaultEnd(begin)

    return { begin, end }
  }

  /**
   * 出発日時のデフォルト値を返す
   *
   * @param {String} base 基準日
   * @returns {String} 出発日時のデフォルト値を返す
   */
  getDefaultBegin (base) {
    // 日付 base の日付情報を取得する
    const d = this.getDate(base)

    // 時刻を C.DEFAULT_BEGIN_TIME にする
    if (d.begin < C.DEFAULT_BEGIN_TIME) {
      d.begin = C.DEFAULT_BEGIN_TIME
    }

    // 日時文字列に変換する
    let begin = `${base} ${d.begin}`

    // allowBegin に収まるように調整する
    const allowBegin = this.getAllowBegin()
    if (base < allowBegin.begin) {
      begin = allowBegin.begin
    } else if (allowBegin.end < base) {
      begin = allowBegin.end
    }

    return begin
  }

  /**
   * 帰着日時のデフォルト値を返す
   *
   * @param {String} begin 出発日時
   * @returns {String} 帰着日時のデフォルト値を返す
   */
  getDefaultEnd (begin) {
    const allowEnd = this.getAllowEnd(begin)

    // end の候補値は allowEnd.begin
    let end = allowEnd.begin

    // begin と end の日付が異なるなら、end を変更しない
    const ymd = time.format('Y-m-d', end)
    if (ymd !== time.format('Y-m-d', begin)) {
      return end
    }

    // 日付 ymd の日付情報を取得する
    const d = this.getDate(ymd)

    // 時刻を C.DEFAULT_END_TIME にする
    if (C.DEFAULT_END_TIME < d.end) {
      d.end = C.DEFAULT_END_TIME
    }

    // 日時文字列に変換する
    end = `${ymd} ${d.end}`

    // allowEnd に収まるように調整する
    if (end < allowEnd.begin) {
      end = allowEnd.begin
    } else if (allowEnd.end < end) {
      end = allowEnd.end
    }

    return end
  }

  /**
   * 24H 用に dates を調整する
   */
  adjustAnytime () {
    for (const ymd in this.dates) {
      this.dates[ymd].opened = true
      this.dates[ymd].begin = '00:00'
      this.dates[ymd].end = '23:30'
    }
  }

  /**
   * 指定日時の日付情報を返す
   *
   * @param {String} ymd 日付文字列
   * @returns {Object} 日付情報を返す
   */
  getDate (ymd) {
    const d = this.dates[ymd]
    if (!d) {
      throw new Error(`Expected to defined '${ymd}' in calendar.dates`)
    }
    return { ...d }
  }

  /**
   * 指定日時以前の営業時間を返す
   *
   * @param {String|Date} t 日時
   * @returns {String} 営業時間の日時文字列を返す
   */
  previousOpenTime (t) {
    const x = time.create(t)

    // 日付情報を取得する
    let hm = time.format('H:i', x)
    let ymd = time.format('Y-m-d', x)
    let d = this.getDate(ymd)

    // 営業開始時刻以上なら
    if (d.opened && d.begin <= hm) {
      if (d.end < hm) {
        hm = d.end
      }
      return `${ymd} ${hm}`
    }

    // 営業日を探す
    x.setHours(0, 0, 0, 0)
    do {
      // x を -1 日する
      x.setDate(x.getDate() - 1)

      // 日付情報を取得する
      ymd = time.format('Y-m-d', x)
      d = this.getDate(ymd)
    } while (!d.opened)

    return `${ymd} ${d.end}`
  }

  /**
   * 指定日時以降の営業時間を返す
   *
   * @param {String|Date} t 日時
   * @returns {String} 営業時間の日時文字列を返す
   */
  nextOpenTime (t) {
    const x = time.create(t)

    // 日付情報を取得する
    let hm = time.format('H:i', x)
    let ymd = time.format('Y-m-d', x)
    let d = this.getDate(ymd)

    // 営業終了時刻以下なら
    if (d.opened && hm <= d.end) {
      if (hm < d.begin) {
        hm = d.begin
      }
      return `${ymd} ${hm}`
    }

    // 営業日を探す
    x.setHours(0, 0, 0, 0)
    do {
      // x を +1 日する
      x.setDate(x.getDate() + 1)

      // ymd の日付情報を取得する
      ymd = time.format('Y-m-d', x)
      d = this.getDate(ymd)
    } while (!d.opened)

    return `${ymd} ${d.begin}`
  }

  /**
   * 日時に時間を加算する
   *
   * @param {String} t 日時文字列
   * @param {String} duration 加算する時間
   * @returns {String} 加算後の日時文字列
   */
  add (t, duration) {
    const x = time.create(t)

    // duration を解釈する
    const m = /^(?<offset>\d+) *(?<unit>hour|day|week|month)s?$/.exec(duration)
    if (!m) {
      throw new Error(`Calendar.add: invalid duration '${duration}'`)
    }
    const offset = m.groups.offset - 0
    const unit = m.groups.unit
    let month

    switch (unit) {
      case 'hour':
        // 時間
        x.setHours(x.getHours() + offset)
        break
      case 'day':
        // 日
        if (this.daybreak === C.DAYBREAK_MIDNIGHT) {
          x.setHours(0, 0, 0, 0)
        }

        x.setDate(x.getDate() + offset)
        break
      case 'week':
        // 週
        if (this.daybreak === C.DAYBREAK_MIDNIGHT) {
          x.setHours(0, 0, 0, 0)
        }

        x.setDate(x.getDate() + (offset * 7))
        break
      case 'month':
        // 月
        if (this.daybreak === C.DAYBREAK_MIDNIGHT) {
          x.setHours(0, 0, 0, 0)
        }

        month = x.getMonth() + offset
        x.setMonth(month)
        if (x.getMonth() === month) {
          // no work
        } else if (this.daybreak === C.DAYBREAK_MIDNIGHT) {
          // 月がオーバーフローしていたら、翌月1日にする
          // 02/30 = 03/02 → 03/01
          x.setDate(month + 1, 1)
        } else {
          // 月がオーバーフローしていたら、当月末日にする
          // 02/30 = 03/02 → 02/28
          x.setMonth(month + 1, 0)
        }
        break
      default:
        throw new Error(`Calendar.add: no supported duration unit '${unit}`)
    }

    return time.format('Y-m-d H:i', x)
  }

  /**
   * 日時から時間を減算する
   *
   * @param {String} t 日時文字列
   * @param {String} duration 減算する時間
   * @returns {String} 減算後の日時文字列
   */
  sub (t, duration) {
    const x = time.create(t)

    // duration を解釈する
    const m = /^(?<offset>\d+) *(?<unit>hour|day|week|month)s?$/.exec(duration)
    if (!m) {
      throw new Error(`Calendar.sub: invalid duration '${duration}'`)
    }
    const offset = m.groups.offset - 0
    const unit = m.groups.unit

    switch (unit) {
      case 'hour':
        // 時間
        x.setHours(x.getHours() - offset)
        break
      case 'day':
        // 日
        x.setDate(x.getDate() - offset)
        break
      default:
        throw new Error(`Calendar.sub: no supported duration unit '${unit}`)
    }

    return time.format('Y-m-d H:i', x)
  }
}
